import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  GitHub as GitHubIcon,
  HelpOutline as HelpOutlineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  fetchTrack,
  fetchLesson,
  fetchLessonProgress,
  fetchTrackProgress,
  saveLessonProgress,
  fetchLearnerRepository,
} from '../api/curriculumApi';
import {
  resolveLessonTaskType,
  buildWorkedExamples,
  buildSimilarProblems,
  buildLessonSolveOverrides,
  formatPracticeJournalEntry,
  upsertPracticeEntry,
  dedupePracticeMarkdown,
  SQL_SAMPLE_SETUP,
} from '../engines/practice/utils/lessonTaskType';
import { recordAuditEvent } from '../api/auditApi';
import {
  fetchJournal,
  saveJournal,
  pushJournalToGitHub,
  runJournalCoach,
} from '../api/repoSyncApi';
import { upsertJournalSection } from '../utils/journalSections';
import { analyzeEvidence } from '../api/evidenceApi';
import EvidenceIntelligenceDialog, { EvidenceIntelligenceSections } from '../components/evidence/EvidenceIntelligenceDialog';
import LessonView from '../components/lesson/LessonView';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import FollowUpTopicsChecklist, {
  followUpTopicsToMarkdown,
  normalizeFollowUpTopics,
} from '../components/common/FollowUpTopicsChecklist';
import { useAuth } from '../auth/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────

const TRACK_ALIASES = {
  'data-analysis': 'data-analyst-job-ready',
  'data-analyst': 'data-analyst-job-ready',
  azure: 'azure-data-engineer',
  'azure-platform': 'azure-platform-engineer',
  'data-science': 'data-scientist-job-ready',
};

const TOOL_PATTERNS_BY_TASK = {
  python: [
    ['pathlib/glob', /\b(pathlib|glob|file discovery|csv files?)\b/i],
    ['pandas', /\b(pandas|dataframe|read_csv|to_csv)\b/i],
    ['DataFrame', /\b(dataframe|df\.|rows?|columns?)\b/i],
    ['read_csv', /\b(read_csv|load csv|csv format)\b/i],
    ['to_csv', /\b(to_csv|write clean output|processed dataset|output folder)\b/i],
    ['functions', /\b(functions?|extract|transform|load)\b/i],
    ['row-count validation', /\b(row counts?|rows in|rows out|rows dropped|validate)\b/i],
    ['logging', /\b(log|logging|log failures?|run_id)\b/i],
    ['try/except', /\b(try\/except|exception|error handling|failures?)\b/i],
    ['virtual environment', /\b(venv|virtual environment|pip install)\b/i],
    ['schema checks', /\b(schema|columns?|data types?)\b/i],
    ['deduplication', /\b(deduplicate|duplicates?|drop_duplicates)\b/i],
    ['unit tests/assertions', /\b(assert|test|pytest|validation rule)\b/i],
  ],
  'data-analysis': [
    ['pandas', /\b(pandas|dataframe|read_csv|read_excel)\b/i],
    ['groupby', /\b(groupby|group by|aggregate|aggregation)\b/i],
    ['merge/join', /\b(merge|join|lookup)\b/i],
    ['filtering', /\b(filter|where|query)\b/i],
    ['data validation', /\b(validate|missing|duplicates?|nulls?|quality)\b/i],
    ['matplotlib/seaborn', /\b(matplotlib|seaborn|plot|chart|visuali[sz]e)\b/i],
    ['pivot table', /\b(pivot|pivot_table)\b/i],
    ['descriptive statistics', /\b(mean|median|mode|standard deviation|summary statistics)\b/i],
    ['Power BI', /\b(power bi|dashboard|report)\b/i],
    ['Excel', /\b(excel|spreadsheet|workbook)\b/i],
  ],
  sql: [
    ['SELECT', /\bselect\b/i],
    ['WHERE', /\bwhere|filter\b/i],
    ['JOIN', /\bjoin\b/i],
    ['GROUP BY', /\bgroup by|aggregate|aggregation\b/i],
    ['HAVING', /\bhaving\b/i],
    ['ORDER BY', /\border by|sort\b/i],
    ['COUNT()', /\bcount\b/i],
    ['SUM()', /\bsum\b/i],
    ['AVG()', /\bavg|average\b/i],
    ['CTE', /\bcte|with\s+\w+\s+as\b/i],
    ['window functions', /\bover\s*\(|row_number|rank|lag|lead\b/i],
  ],
  excel: [
    ['Excel Table', /\b(table|structured reference)\b/i],
    ['Sort', /\bsort\b/i],
    ['Filter', /\bfilter\b/i],
    ['Named ranges', /\bnamed range|name manager\b/i],
    ['SUMIF/SUMIFS', /\bsumifs?\b/i],
    ['COUNTIF/COUNTIFS', /\bcountifs?\b/i],
    ['XLOOKUP', /\bxlookup\b/i],
    ['PivotTable', /\bpivot\s*table|pivottable\b/i],
    ['Power Query', /\bpower query\b/i],
    ['Charts', /\bchart|visual\b/i],
    ['Conditional Formatting', /\bconditional formatting\b/i],
  ],
  azure: [
    ['ADF pipeline', /\b(adf|data factory|pipeline)\b/i],
    ['linked service', /\blinked service\b/i],
    ['dataset', /\bdataset\b/i],
    ['trigger', /\btrigger\b/i],
    ['watermark', /\bwatermark|incremental\b/i],
    ['ADLS Gen2', /\b(adls|data lake|storage account|blob)\b/i],
    ['Bronze/Silver/Gold', /\b(bronze|silver|gold|medallion)\b/i],
    ['Delta Lake', /\b(delta|acid|time travel)\b/i],
    ['Databricks', /\b(databricks|notebook|cluster)\b/i],
    ['PySpark', /\b(pyspark|spark)\b/i],
    ['Microsoft Fabric', /\b(fabric|onelake|lakehouse)\b/i],
    ['Key Vault', /\b(key vault|secret)\b/i],
    ['Managed Identity', /\bmanaged identity\b/i],
    ['Application Insights', /\b(application insights|monitor|alert)\b/i],
    ['dbt/dbt-style models', /\b(dbt|analytics engineering|staging|marts?)\b/i],
    ['Snowflake comparison', /\b(snowflake)\b/i],
  ],
};

const DEFAULT_TOOLS_BY_TASK = Object.fromEntries(
  Object.entries(TOOL_PATTERNS_BY_TASK).map(([key, patterns]) => [
    key,
    patterns.map(([label]) => label),
  ]),
);

const AZURE_TOOL_SIGNAL = /\b(adf|azure|data factory|fabric|onelake|lakehouse|databricks|spark|pyspark|synapse|adls|blob|storage account|key vault|managed identity|bronze|silver|gold|medallion|delta lake|purview|dp-700|snowflake)\b/i;

function resolveToolPatternKey(lesson, taskType, text) {
  if (TOOL_PATTERNS_BY_TASK[taskType] && taskType !== 'azure') {
    return taskType;
  }

  const pathKey = lesson?.learning_path_key || '';
  if (pathKey === 'azure-data-engineer' && AZURE_TOOL_SIGNAL.test(text)) {
    return 'azure';
  }

  return TOOL_PATTERNS_BY_TASK[taskType] ? taskType : 'data-analysis';
}

function extractLessonTools(lesson, taskType) {
  const text = [
    lesson?.title,
    lesson?.concept,
    lesson?.lesson_prompt,
    lesson?.deliverable,
  ].filter(Boolean).join(' ');
  const patternKey = resolveToolPatternKey(lesson, taskType, text);
  const patterns = TOOL_PATTERNS_BY_TASK[patternKey] || TOOL_PATTERNS_BY_TASK['data-analysis'];
  const matchedTools = patterns
    .filter(([, pattern]) => pattern.test(text))
    .map(([label]) => label);
  const defaultTools = DEFAULT_TOOLS_BY_TASK[patternKey] || [];
  const tools = [
    ...matchedTools,
    ...defaultTools.filter((tool) => !matchedTools.includes(tool)),
  ];
  return tools.length ? tools.slice(0, patternKey === 'azure' ? 16 : 14) : null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const serializeFollowUpTopics = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return JSON.stringify(value);
  return '';
};

const JOURNAL_NOTE_EXAMPLES = [
  {
    label: 'Concept learned',
    text: 'I learned that `glob.glob()` can return files in different orders on different machines, so I used `files = sorted(glob.glob("data/*.csv"))` before loading CSV files.',
  },
  {
    label: 'Mistake fixed',
    text: 'My ETL output changed because duplicate rows were not removed before aggregation. I fixed it by checking `df.duplicated().sum()` and using `drop_duplicates()` on the correct business key.',
  },
  {
    label: 'Evidence produced',
    text: 'I created a validation check that compares input row count, cleaned row count, and rejected row count, then saved the result in `notes/week-01/etl-checks.md`.',
  },
  {
    label: 'Interview-ready reflection',
    text: 'If this happened in production, I would log file count, row count, null count, validation failures, output path, and exception details so the failure can be traced quickly.',
  },
];

const FOLLOW_UP_TOPIC_EXAMPLES = [
  'Python logging levels: INFO vs WARNING vs ERROR',
  'How to write data quality checks before loading data',
  'Difference between deduplication key and primary key',
  'Microsoft Learn: Azure Data Factory monitoring',
  'When to use pandas validation vs SQL validation',
];

const JOURNAL_VOICE_EXAMPLES = [
  {
    label: 'Use first person for ownership',
    weak: 'The script was updated.',
    strong: 'I updated the ETL script to sort input files before loading them.',
  },
  {
    label: 'Use neutral language for proof',
    weak: 'I can do data validation. I can clean data. I can write logs.',
    strong: 'The work demonstrates data validation, cleaning decisions, and production-style logging.',
  },
  {
    label: 'Use first person for reflection',
    weak: 'Logging is important.',
    strong: 'I would explain this in an interview as: logging gives the team a trace of what changed, where it failed, and how many rows were affected.',
  },
];

const parseBullets = (str) => {
  if (!str?.trim()) return [''];
  const items = str
    .split(/[;\n]+/)
    .map((s) => s.replace(/^\d+\.\s*/, '').trim())
    .filter(Boolean);
  return items.length ? items : [''];
};

const joinBullets = (items) =>
  items
    .filter((s) => s.trim())
    .map((s, i) => `${i + 1}. ${s.trim()}`)
    .join('\n');

const journalPointLabel = (text = '') => {
  const value = text.toLowerCase();
  if (/\b(built|created|implemented|designed|wrote|added)\b/.test(value)) return 'Built';
  if (/\b(used|applied|ran|configured|processed|loaded)\b/.test(value)) return 'Applied';
  if (/\b(explain|learned|understood|question|interview)\b/.test(value)) return 'Explained';
  if (/\b(verified|checked|tested|logged|validated|result|rows|output)\b/.test(value)) return 'Verified';
  if (/\b(fix|fixed|because|prevents|production|ci|failure)\b/.test(value)) return 'Why it matters';
  // No keyword matched — previously this fell back to a POSITIONAL default (whatever
  // label sat at this bullet's index), which mislabeled any first bullet as "Built"
  // regardless of content (e.g. "concept clinic was awesome"). Honest > confident: say
  // "Note" rather than guess a category the text gave no evidence for.
  return 'Note';
};

const formatJournalPoint = (text = '') => {
  const cleaned = text.trim();
  if (!cleaned) return '';
  return `- **${journalPointLabel(cleaned)}:** ${cleaned}`;
};

// A lesson is only "complete" once all tasks are checked AND confidence has been
// rated — confidence=0 is the backend's unset default (see LessonProgress.confidence),
// so a truthy value is the only signal the learner actually rated themselves. Used
// everywhere is_completed gets computed so the sidebar's unlockedSlugs (driven by
// is_completed) can never unlock the next lesson ahead of the "Continue" button's own
// gate in LessonView — otherwise a learner could bypass the confidence requirement
// entirely by clicking straight into the next lesson from the sidebar.
const isLessonReady = (completedCount, totalTasks, confidenceValue) =>
  (totalTasks === 0 || completedCount >= totalTasks) && Boolean(confidenceValue);

const flattenLessons = (track) => {
  if (!track) return [];
  let weekNumber = 0;
  return track.courses
    .flatMap((course) =>
      course.modules.flatMap((module) => {
        weekNumber += 1;
        return module.lessons.map((lesson, index) => ({
          ...lesson,
          course_title: course.name,
          week_number: weekNumber,
          week_title: module.name,
          lesson_number_in_week: index + 1,
        }));
      })
    )
    .sort((a, b) => a.sort_order - b.sort_order);
};

const compactLessonLabel = (title = '') => {
  const raw = String(title || '').trim();
  if (!raw) return 'Lesson';
  const [prefix, ...rest] = raw.split(':');
  if (!rest.length) return raw.length > 34 ? `${raw.slice(0, 31)}...` : raw;
  const action = prefix.trim();
  const topic = rest.join(':').trim();
  const firstTopic = topic.split(',')[0].trim() || topic;
  const compactTopic = firstTopic.length > 24 ? `${firstTopic.slice(0, 21)}...` : firstTopic;
  return `${action}: ${compactTopic}`;
};

const ROLE_ACTION_LABELS = {
  'data-analyst-job-ready': {
    learn: 'Understand the business question',
    practice: 'Build the workbook/report',
    'clean and validate': 'Clean and check the data',
    analyze: 'Find the business answer',
    analyse: 'Find the business answer',
    visualize: 'Show the answer clearly',
    visualise: 'Show the answer clearly',
    'portfolio proof': 'Package the evidence',
    review: 'Explain it like an analyst',
  },
  'azure-data-engineer': {
    learn: 'Understand the data problem',
    practice: 'Build the pipeline step',
    design: 'Choose the data architecture',
    build: 'Implement the pipeline',
    'clean and validate': 'Validate the data flow',
    monitor: 'Prove the pipeline can be owned',
    'portfolio proof': 'Package the engineering evidence',
    review: 'Defend the trade-offs',
  },
  'azure-platform-engineer': {
    learn: 'Understand the platform risk',
    practice: 'Build the platform control',
    design: 'Choose the architecture',
    build: 'Deploy the smallest safe version',
    secure: 'Add guardrails',
    govern: 'Add guardrails',
    monitor: 'Prove it can be operated',
    operate: 'Prove it can be owned',
    'portfolio proof': 'Package the platform evidence',
    review: 'Defend the trade-offs',
  },
  'python-job-ready': {
    learn: 'Understand the programming idea',
    practice: 'Write the working code',
    test: 'Prove the code works',
    debug: 'Find and fix the failure',
    automate: 'Make it repeatable',
    'portfolio proof': 'Package the code evidence',
    review: 'Explain the implementation',
  },
  'sql-job-ready': {
    learn: 'Understand the data question',
    practice: 'Write the query',
    design: 'Choose the table strategy',
    optimize: 'Improve the query plan',
    optimise: 'Improve the query plan',
    administer: 'Operate the database safely',
    'portfolio proof': 'Package the SQL evidence',
    review: 'Explain the query decisions',
  },
  'data-scientist-job-ready': {
    learn: 'Understand the modelling question',
    practice: 'Build the notebook/model',
    prepare: 'Prepare the dataset',
    model: 'Train and evaluate',
    evaluate: 'Prove model quality',
    deploy: 'Package the model for use',
    'portfolio proof': 'Package the model evidence',
    review: 'Defend the modelling choices',
  },
};

const resolveTrackLabelKey = (track = {}, fallbackSlug = '') => (
  track?.slug ||
  track?.learning_path_key ||
  TRACK_ALIASES[fallbackSlug] ||
  fallbackSlug ||
  ''
);

const normalizeLabelText = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/\bweek\s+\d+\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const labelTokens = (value = '') =>
  normalizeLabelText(value)
    .split(/\s+/)
    .filter((token) => token.length > 2 && !['and', 'the', 'with', 'for'].includes(token));

const topicMatchesContext = (topic = '', context = '') => {
  const normalizedTopic = normalizeLabelText(topic);
  const normalizedContext = normalizeLabelText(context);
  if (!normalizedTopic || !normalizedContext) return false;
  if (normalizedTopic.includes(normalizedContext) || normalizedContext.includes(normalizedTopic)) {
    return true;
  }

  const topicWords = labelTokens(topic);
  const contextWords = labelTokens(context);
  if (!topicWords.length || !contextWords.length) return false;

  const contextSet = new Set(contextWords);
  const sharedWords = topicWords.filter((word) => contextSet.has(word));
  const leadingWords = topicWords.slice(0, 3);
  const leadingMatch = leadingWords.length >= 2 && leadingWords.every((word) => contextSet.has(word));
  return leadingMatch || sharedWords.length >= Math.min(3, topicWords.length);
};

const displayLessonTitle = (lesson = {}, track = {}, fallbackSlug = '', options = {}) => {
  const raw = String(lesson?.title || '').trim();
  if (!raw) return 'Lesson';
  const [prefix, ...rest] = raw.split(':');
  if (!rest.length) return raw;

  const labelKey = resolveTrackLabelKey(track, fallbackSlug);
  const action = prefix.trim();
  const mappedAction = ROLE_ACTION_LABELS[labelKey]?.[action.toLowerCase()];
  if (!mappedAction) return raw;

  const topic = rest.join(':').trim();
  const contextTopic = options.contextTopic || lesson.week_title || lesson.module_name || '';
  const topicRepeatsContext =
    options.trimRepeatedTopic &&
    topicMatchesContext(topic, contextTopic);
  if (!topic || topicRepeatsContext) return mappedAction;

  return topic ? `${mappedAction}: ${topic}` : mappedAction;
};

const parseNotesForMarkdown = (raw) => {
  if (!raw?.trim()) return '';
  const tokens = raw
    .split(/;|\n/)
    .map((t) => t.trim())
    .filter(Boolean);
  const gitLines = [];
  const actionItems = [];
  tokens.forEach((token) => {
    if (/^git\s+\S/i.test(token)) {
      gitLines.push(token);
    } else {
      const stripped = token.replace(/^\d+[.)]\s*/, '').trim();
      if (stripped) actionItems.push(stripped);
    }
  });
  const output = [];
  if (actionItems.length)
    output.push(actionItems.map((item) => formatJournalPoint(item)).join('\n'));
  if (gitLines.length)
    output.push(`\`\`\`bash\n${gitLines.join('\n')}\n\`\`\``);
  return output.join('\n\n');
};

const formatJournalTimestamp = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const extractWeekNumber = (value = '') => {
  const match = String(value || '').match(/\bweek\s*(\d+)\b/i);
  return match ? Number(match[1]) : null;
};

const cleanWeekTitle = (value = '') =>
  String(value || 'Learning block')
    .replace(/^\s*week\s*\d+\s*[:.\-–—]?\s*/i, '')
    .trim() || 'Learning block';

const journalAnchor = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/`/g, '')
    .replace(/&/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

const resolveJournalWeek = (lesson = {}, fallbackIndex = 1) => {
  const explicitWeek = extractWeekNumber(lesson.week_title) || extractWeekNumber(lesson.phase);
  const weekNumber = explicitWeek || lesson.week_number || fallbackIndex;
  const title = cleanWeekTitle(lesson.week_title || lesson.phase || 'Learning block');
  return {
    number: weekNumber,
    title,
    heading: title ? `Week ${weekNumber}: ${title}` : `Week ${weekNumber}`,
  };
};

const buildLessonsMarkdown = (track, cache, flatLessons) => {
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const title = track?.name || 'Learning';
  const entries = flatLessons
    .map((lesson, index) => {
      const prog = cache[lesson.slug];
      const hasContent =
        prog?.completed_task_indices?.length ||
        prog?.notes ||
        prog?.follow_up_topics ||
        prog?.practice_markdown;
      if (!hasContent) return null;
      return {
        lesson,
        prog,
        week: resolveJournalWeek(lesson, index + 1),
      };
    })
    .filter(Boolean);
  const weekSummary = entries.reduce((acc, entry) => {
    const key = `${entry.week.number}:${entry.week.title}`;
    if (!acc.has(key)) {
      acc.set(key, {
        ...entry.week,
        lessons: 0,
        completed: 0,
        lastUpdated: null,
      });
    }
    const item = acc.get(key);
    item.lessons += 1;
    if (entry.prog?.is_completed) item.completed += 1;
    if (entry.prog?.updated_at) {
      const current = item.lastUpdated ? new Date(item.lastUpdated).getTime() : 0;
      const candidate = new Date(entry.prog.updated_at).getTime();
      if (!Number.isNaN(candidate) && candidate > current) item.lastUpdated = entry.prog.updated_at;
    }
    return acc;
  }, new Map());

  const lines = [
    `# ${title} Learning Journal`,
    '',
    `> Last updated: ${today}`,
    '',
    '## Summary',
    '',
    `- Track: ${title}`,
    `- Weeks with evidence: ${weekSummary.size}`,
    `- Lesson entries captured: ${entries.length}`,
    '',
  ];

  if (weekSummary.size) {
    lines.push('| Week | Evidence entries | Completed | Last updated |', '| --- | ---: | ---: | --- |');
    Array.from(weekSummary.values())
      .sort((a, b) => a.number - b.number)
      .forEach((week) => {
        lines.push(
          `| [${week.heading}](#${journalAnchor(week.heading)}) | ${week.lessons} | ${week.completed} | ${formatJournalTimestamp(week.lastUpdated)} |`
        );
      });
    lines.push('');
  }

  lines.push(
    '---',
    '',
  );
  let entryCount = 0;
  let activeWeekKey = '';
  entries.forEach(({ lesson, prog, week }) => {
    entryCount += 1;
    const weekKey = `${week.number}:${week.title}`;
    if (weekKey !== activeWeekKey) {
      activeWeekKey = weekKey;
      lines.push(`## ${week.heading}`, '');
    }
    lines.push(`### Lesson ${lesson.lesson_number_in_week || entryCount}: ${lesson.title}`, '');
    lines.push('| Field | Value |', '| --- | --- |');
    lines.push(`| Lesson slug | \`${lesson.slug}\` |`);
    if (lesson.phase) lines.push(`| Phase | ${lesson.phase} |`);
    lines.push(`| Saved/updated | ${formatJournalTimestamp(prog?.updated_at)} |`);
    lines.push(`| Completed tasks | ${prog?.completed_task_indices?.length || 0} |`);
    lines.push(`| Lesson complete | ${prog?.is_completed ? 'Yes' : 'In progress'} |`, '');
    if (prog?.completed_task_indices?.length && lesson.tasks?.length) {
      lines.push('### Tasks Completed', '');
      prog.completed_task_indices.forEach((index) => {
        const task = lesson.tasks[index];
        if (task) lines.push(`- [x] ${task.text}`);
      });
      lines.push('');
    }
    if (prog?.notes?.trim())
      lines.push('### What I Did', '', parseNotesForMarkdown(prog.notes), '');
    if (prog?.follow_up_topics)
      lines.push(
        '### To Explore Further',
        '',
        followUpTopicsToMarkdown(prog.follow_up_topics),
        ''
      );
    if (prog?.practice_markdown?.trim())
      lines.push('### My Practice Work', '', prog.practice_markdown.trim(), '');
    lines.push('---', '');
  });
  if (!entryCount) lines.push('No lessons recorded yet.', '');
  return lines.join('\n');
};

// Saving used to be a raw `saveJournal(trackSlug, markdown)` — a full-file
// replace that silently wiped every cohort-tab entry (Hackathon, Concept
// Clinic, Show & Tell, Mock Interview) any time a learner saved a lesson
// note, since buildLessonsMarkdown rebuilds the journal purely from lesson
// progress with no idea the cohort tab had written anything there. This
// fetches the current file first and only replaces the lesson-journal
// section, leaving the cohort-tab section (and anything else) untouched.
const saveLessonsJournal = async (trackSlug, lessonMarkdown) => {
  const current = await fetchJournal(trackSlug).catch(() => ({ markdown: '' }));
  const merged = upsertJournalSection(current?.markdown, 'lessons', lessonMarkdown);
  return saveJournal(trackSlug, merged);
};

// ── Sub-components ────────────────────────────────────────────────────────────

/** Slim progress ring used in the hero band */
const ProgressRing = ({ value, size = 48, stroke = 3 }) => {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,0.85)" strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
          {value}%
        </Typography>
      </Box>
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const TrackRoadmapPage = ({ trackSlug: explicitTrackSlug }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const canReviewAllLessons = ['admin', 'platform_owner'].includes(user?.role);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const requestedSlug = explicitTrackSlug || params.trackSlug;
  const trackSlug = TRACK_ALIASES[requestedSlug] || requestedSlug;
  const lessonParam = searchParams.get('lesson');

  const [track, setTrack] = useState(null);
  const [trackLoading, setTrackLoading] = useState(true);
  const [trackError, setTrackError] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [lessonDetail, setLessonDetail] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [progressCache, setProgressCache] = useState({});
  const [noteItems, setNoteItems] = useState(['']);
  const [followUpDraft, setFollowUpDraft] = useState('');
  const [noteSaveStatus, setNoteSaveStatus] = useState('');
  const [phaseTabIndex, setPhaseTabIndex] = useState(0);
  const [weekMenuAnchor, setWeekMenuAnchor] = useState(null);
  const [learnerRepo, setLearnerRepo] = useState({ repo_url: '', has_github_pat: false });
  const [githubPushState, setGithubPushState] = useState('idle');
  const [githubPushResult, setGithubPushResult] = useState(null);
  const [journalHelpOpen, setJournalHelpOpen] = useState(false);
  const [journalCoachOpen, setJournalCoachOpen] = useState(false);
  const [journalCoachState, setJournalCoachState] = useState('idle');
  const [journalCoachResult, setJournalCoachResult] = useState(null);
  const [portfolioReviewOpen, setPortfolioReviewOpen] = useState(false);
  const [noteCoachOpen, setNoteCoachOpen] = useState(false);
  const [noteCoachState, setNoteCoachState] = useState('idle');
  const [noteCoachResult, setNoteCoachResult] = useState(null);
  const [noteCoachError, setNoteCoachError] = useState('');
  const [portfolioAiState, setPortfolioAiState] = useState('idle');
  const [portfolioAiResult, setPortfolioAiResult] = useState(null);
  const [portfolioAiError, setPortfolioAiError] = useState('');
  const [interviewPrepState, setInterviewPrepState] = useState('idle');
  const [interviewPrepResult, setInterviewPrepResult] = useState(null);
  const [interviewPrepError, setInterviewPrepError] = useState('');
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [secondaryTitle, setSecondaryTitle] = useState('');
  const [secondaryAnalysisType, setSecondaryAnalysisType] = useState('linkedin-post');
  const [secondaryState, setSecondaryState] = useState('idle');
  const [secondaryResult, setSecondaryResult] = useState(null);
  const [secondaryError, setSecondaryError] = useState('');

  const flatLessons = useMemo(() => flattenLessons(track), [track]);
  const notePreviewMarkdown = useMemo(
    () => parseNotesForMarkdown(joinBullets(noteItems)),
    [noteItems]
  );

  const phaseChunks = useMemo(
    () =>
      (track?.courses ?? []).map((course) => ({
        phase: course.name,
        courseSlug: course.slug,
        modules: course.modules,
        lessons: course.modules
          .flatMap((m) => m.lessons)
          .sort((a, b) => a.sort_order - b.sort_order),
      })),
    [track]
  );

  const currentChunk = phaseChunks[phaseTabIndex];

  const currentLesson = useMemo(
    () => flatLessons.find((l) => l.slug === selectedSlug) || flatLessons[0] || null,
    [flatLessons, selectedSlug]
  );

  const nextLesson = useMemo(() => {
    const idx = flatLessons.findIndex((l) => l.slug === selectedSlug);
    if (idx === -1 || idx >= flatLessons.length - 1) return null;
    return flatLessons[idx + 1];
  }, [flatLessons, selectedSlug]);

  const currentDisplayTitle = useMemo(
    () => displayLessonTitle(
      { ...currentLesson, ...lessonDetail },
      track,
      trackSlug,
      { trimRepeatedTopic: true }
    ),
    [lessonDetail, currentLesson, track, trackSlug]
  );

  const nextDisplayTitle = useMemo(
    () => (nextLesson
      ? displayLessonTitle(nextLesson, track, trackSlug, { trimRepeatedTopic: true })
      : null),
    [nextLesson, track, trackSlug]
  );

  const currentProgress = useMemo(
    () =>
      progressCache[selectedSlug] || {
        completed_task_indices: [],
        notes: '',
        follow_up_topics: '',
        confidence: 0,
      },
    [progressCache, selectedSlug]
  );

  const completedIndices = currentProgress.completed_task_indices;

  const overallCompletedTasks = useMemo(
    () => flatLessons.reduce((sum, l) => sum + (l.completed_task_count || 0), 0),
    [flatLessons]
  );
  const overallTotalTasks = useMemo(
    () => flatLessons.reduce((sum, l) => sum + (l.total_task_count || 0), 0),
    [flatLessons]
  );
  const overallProgress =
    overallTotalTasks > 0
      ? Math.round((overallCompletedTasks / overallTotalTasks) * 100)
      : 0;
  const dayProgress = lessonDetail
    ? Math.round((completedIndices.length / Math.max(lessonDetail.tasks.length, 1)) * 100)
    : 0;
  const nextSlug = useMemo(
    () =>
      (
        flatLessons.find((l) => !l.is_completed) ||
        flatLessons[flatLessons.length - 1]
      )?.slug || null,
    [flatLessons]
  );

  const unlockedSlugs = useMemo(() => {
    if (canReviewAllLessons) {
      return new Set(flatLessons.map((l) => l.slug).filter(Boolean));
    }
    const completed = flatLessons.filter((l) => l.is_completed).map((l) => l.slug);
    return new Set([...completed, nextSlug].filter(Boolean));
  }, [canReviewAllLessons, flatLessons, nextSlug]);

  const activeModuleIndex = useMemo(() => {
    if (!currentLesson || !currentChunk) return -1;
    return currentChunk.modules.findIndex((m) =>
      m.lessons.some((l) => l.slug === currentLesson.slug)
    );
  }, [currentChunk, currentLesson]);

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!trackSlug) {
      setTrackError('Track slug is missing.');
      setTrackLoading(false);
      return;
    }
    setTrackLoading(true);
    setTrackError('');
    setTrack(null);
    setSelectedSlug(null);
    setProgressCache({});

    fetchTrack(trackSlug)
      .then((data) => {
        setTrack(data);
        const all = flattenLessons(data);
        const fromParam = lessonParam && all.find((l) => l.slug === lessonParam);
        const first = all.find((l) => !l.is_completed) || all[0];
        setSelectedSlug((fromParam || first)?.slug || null);
        fetchTrackProgress(trackSlug)
          .then((progress) => setProgressCache(progress || {}))
          .catch(() => {});
      })
      .catch((err) => setTrackError(err.message))
      .finally(() => setTrackLoading(false));

  }, [lessonParam, trackSlug]);

  useEffect(() => {
    if (!track?.learning_path_key) return;
    fetchLearnerRepository(track.learning_path_key).then(setLearnerRepo).catch(() => { });
  }, [track?.learning_path_key]);

  useEffect(() => {
    if (!track?.learning_path_key) return;
    const refresh = () =>
      fetchLearnerRepository(track.learning_path_key).then(setLearnerRepo).catch(() => { });
    window.addEventListener('github-settings-updated', refresh);
    return () => window.removeEventListener('github-settings-updated', refresh);
  }, [track?.learning_path_key]);

  useEffect(() => {
    if (!track || !selectedSlug) return;
    const index = phaseChunks.findIndex((chunk) =>
      chunk.lessons.some((l) => l.slug === selectedSlug)
    );
    if (index !== -1) setPhaseTabIndex(index);
  }, [selectedSlug, phaseChunks, track]);

  useEffect(() => {
    if (!selectedSlug) return;
    setLessonLoading(true);
    setLessonDetail(null);

    Promise.all([fetchLesson(selectedSlug), fetchLessonProgress(selectedSlug)])
      .then(([detail, progress]) => {
        setLessonDetail(detail);
        const rawMarkdown = progress?.practice_markdown ?? '';
        const practice_markdown = dedupePracticeMarkdown(rawMarkdown);
        const prog = {
          completed_task_indices: progress?.completed_task_indices ?? [],
          notes: progress?.notes ?? '',
          follow_up_topics: progress?.follow_up_topics ?? '',
          practice_markdown,
          is_completed: progress?.is_completed ?? false,
          confidence: progress?.confidence ?? 0,
          updated_at: progress?.updated_at ?? null,
        };
        setProgressCache((prev) => ({ ...prev, [selectedSlug]: prog }));
        // Persist the cleaned markdown back to DB if duplicates were removed
        if (practice_markdown !== rawMarkdown) {
          saveLessonProgress(selectedSlug, {
            completed_task_indices: prog.completed_task_indices,
            notes: prog.notes,
            follow_up_topics: prog.follow_up_topics,
            is_completed: prog.is_completed,
            confidence: prog.confidence,
            practice_markdown,
          }).catch(() => {});
        }
        setNoteItems(parseBullets(prog.notes));
        setFollowUpDraft(normalizeFollowUpTopics(prog.follow_up_topics));
        setNoteSaveStatus('');
      })
      .catch(() => {
        setNoteItems(['']);
        setFollowUpDraft('');
      })
      .finally(() => setLessonLoading(false));

    recordAuditEvent('lesson_opened', {
      tracker: trackSlug,
      lesson_slug: selectedSlug,
    }).catch(() => { });
  }, [selectedSlug, trackSlug]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const goToSlug = useCallback(
    (slug) => {
      if (unlockedSlugs.has(slug)) setSelectedSlug(slug);
    },
    [unlockedSlugs]
  );

  const jumpToPhase = useCallback(
    (phaseIndex) => {
      const chunk = phaseChunks[phaseIndex];
      if (!chunk) return;
      const target =
        chunk.lessons.find((l) => unlockedSlugs.has(l.slug) && !l.is_completed) ||
        chunk.lessons.find((l) => unlockedSlugs.has(l.slug));
      if (target) setSelectedSlug(target.slug);
      setPhaseTabIndex(phaseIndex);
    },
    [phaseChunks, unlockedSlugs]
  );

  // Jumps to a given module (week/sprint) within the active phase — lands on its
  // first unlocked-and-incomplete lesson, or its first unlocked lesson otherwise.
  const goToModule = useCallback(
    (module) => {
      if (!module) return;
      const target =
        module.lessons.find((l) => unlockedSlugs.has(l.slug) && !l.is_completed) ||
        module.lessons.find((l) => unlockedSlugs.has(l.slug));
      if (target) setSelectedSlug(target.slug);
    },
    [unlockedSlugs]
  );

  const updateTrackLessonProgress = useCallback(
    (completedTaskCount, isCompleted) => {
      setTrack((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        for (const course of next.courses)
          for (const module of course.modules)
            for (const lesson of module.lessons)
              if (lesson.slug === selectedSlug) {
                lesson.completed_task_count = completedTaskCount;
                lesson.is_completed = isCompleted;
              }
        return next;
      });
    },
    [selectedSlug]
  );

  const toggleTask = useCallback(
    async (index) => {
      if (!selectedSlug || !lessonDetail) return;
      const nextCompleted = completedIndices.includes(index)
        ? completedIndices.filter((i) => i !== index)
        : [...completedIndices, index].sort((a, b) => a - b);
      const updatedProg = {
        ...currentProgress,
        completed_task_indices: nextCompleted,
        is_completed: isLessonReady(nextCompleted.length, lessonDetail.tasks.length, currentProgress.confidence),
        updated_at: new Date().toISOString(),
      };
      setProgressCache((c) => ({ ...c, [selectedSlug]: updatedProg }));
      recordAuditEvent(
        completedIndices.includes(index) ? 'task_reopened' : 'task_completed',
        { tracker: trackSlug, lesson_slug: selectedSlug, task: index }
      ).catch(() => { });
      try {
        const isCompleted = isLessonReady(nextCompleted.length, lessonDetail.tasks.length, currentProgress.confidence);
        const result = await saveLessonProgress(selectedSlug, {
          completed_task_indices: nextCompleted,
          notes: currentProgress.notes,
          follow_up_topics: serializeFollowUpTopics(currentProgress.follow_up_topics),
          is_completed: isCompleted,
          confidence: currentProgress.confidence || 0,
          practice_markdown: currentProgress.practice_markdown || '',
        });
        setProgressCache((c) => ({
          ...c,
          [selectedSlug]: {
            ...updatedProg,
            is_completed: result?.is_completed ?? isCompleted,
            updated_at: result?.updated_at ?? updatedProg.updated_at,
          },
        }));
        updateTrackLessonProgress(nextCompleted.length, isCompleted);
      } catch { /* optimistic — reconcile on reload */ }
    },
    [completedIndices, currentProgress, lessonDetail, selectedSlug, trackSlug, updateTrackLessonProgress]
  );

  const setLessonConfidence = useCallback(
    async (value) => {
      if (!selectedSlug || !lessonDetail) return;
      const isCompleted = isLessonReady(completedIndices.length, lessonDetail.tasks.length, value);
      const updatedProg = { ...currentProgress, confidence: value, is_completed: isCompleted, updated_at: new Date().toISOString() };
      setProgressCache((c) => ({ ...c, [selectedSlug]: updatedProg }));
      try {
        const result = await saveLessonProgress(selectedSlug, {
          completed_task_indices: completedIndices,
          notes: currentProgress.notes,
          follow_up_topics: serializeFollowUpTopics(currentProgress.follow_up_topics),
          is_completed: isCompleted,
          confidence: value,
          practice_markdown: currentProgress.practice_markdown || '',
        });
        setProgressCache((c) => ({
          ...c,
          [selectedSlug]: {
            ...updatedProg,
            is_completed: result?.is_completed ?? isCompleted,
            updated_at: result?.updated_at ?? updatedProg.updated_at,
          },
        }));
        updateTrackLessonProgress(completedIndices.length, isCompleted);
      } catch { /* optimistic — reconcile on reload */ }
    },
    [completedIndices, currentProgress, lessonDetail, selectedSlug, updateTrackLessonProgress]
  );

  const saveLessonNotes = async () => {
    if (!selectedSlug || !lessonDetail) return;
    const notesStr = joinBullets(noteItems);
    const serializedTopics = serializeFollowUpTopics(followUpDraft);
    const updatedProg = { ...currentProgress, notes: notesStr, follow_up_topics: serializedTopics };
    setNoteSaveStatus('Saving…');
    const readyNow = isLessonReady(completedIndices.length, lessonDetail.tasks.length, currentProgress.confidence);
    try {
      const result = await saveLessonProgress(selectedSlug, {
        completed_task_indices: completedIndices,
        notes: notesStr,
        follow_up_topics: serializedTopics,
        is_completed: readyNow,
        confidence: currentProgress.confidence || 0,
        practice_markdown: progressCache[selectedSlug]?.practice_markdown || '',
      });
      const savedProg = {
        ...updatedProg,
        is_completed: result?.is_completed ?? readyNow,
        updated_at: result?.updated_at ?? new Date().toISOString(),
      };
      setProgressCache((c) => ({ ...c, [selectedSlug]: savedProg }));
      recordAuditEvent('lesson_notes_saved', {
        tracker: trackSlug,
        lesson_slug: selectedSlug,
        has_follow_up_topics: Array.isArray(followUpDraft)
          ? followUpDraft.length > 0
          : Boolean(followUpDraft),
      }).catch(() => { });
      const updatedCache = { ...progressCache, [selectedSlug]: savedProg };
      const markdown = buildLessonsMarkdown(track, updatedCache, flatLessons);
      try {
        await saveLessonsJournal(trackSlug, markdown);
        setNoteSaveStatus('Saved — journal updated');
      } catch {
        setNoteSaveStatus('Saved — journal skipped');
      }
    } catch {
      setNoteSaveStatus('Save failed. Try again.');
    }
  };

  const pushToGitHub = async () => {
    setGithubPushState('pushing');
    setGithubPushResult(null);
    try {
      const result = await pushJournalToGitHub(trackSlug);
      setGithubPushState('done');
      setGithubPushResult(result);
      recordAuditEvent('github_push_completed', { tracker: trackSlug }).catch(() => { });
    } catch (error) {
      setGithubPushState('error');
      setGithubPushResult({ error: error.message || 'Push failed.' });
    }
  };

  const exportLessons = () => {
    const markdown = buildLessonsMarkdown(track, progressCache, flatLessons);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${trackSlug}-lessons-${new Date().toISOString().slice(0, 10)}.md`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const runCoach = async () => {
    setJournalCoachOpen(true);
    setJournalCoachState('loading');
    setJournalCoachResult(null);
    try {
      const markdown = buildLessonsMarkdown(track, progressCache, flatLessons);
      const result = await runJournalCoach(trackSlug, {
        markdown,
        trackTitle: track?.title || track?.name || trackSlug,
      });
      setJournalCoachResult(result);
      setJournalCoachState('done');
      recordAuditEvent('journal_coach_ran', {
        tracker: trackSlug,
        mode: result.mode,
      }).catch(() => { });
    } catch (error) {
      setJournalCoachResult({ error: error.message || 'Journal coach failed.' });
      setJournalCoachState('error');
    }
  };

  const runNoteCoach = async () => {
    setNoteCoachOpen(true);
    setNoteCoachState('loading');
    setNoteCoachError('');
    try {
      const result = await analyzeEvidence({
        content: joinBullets(noteItems),
        analysisType: 'lesson-note-coach',
        context: {
          track_title: track?.title || track?.name || trackSlug,
          tracker_id: trackSlug,
          lesson_slug: selectedSlug,
        },
      });
      setNoteCoachResult(result);
      setNoteCoachState('done');
      recordAuditEvent('lesson_note_coach_ran', {
        tracker: trackSlug,
        lesson_slug: selectedSlug,
        mode: result.mode,
      }).catch(() => { });
    } catch (error) {
      setNoteCoachError(error.message || 'Lesson note coach failed.');
      setNoteCoachState('error');
    }
  };

  const runPortfolioAi = async () => {
    setPortfolioAiState('loading');
    setPortfolioAiError('');
    try {
      const markdown = buildLessonsMarkdown(track, progressCache, flatLessons);
      const result = await analyzeEvidence({
        content: markdown,
        analysisType: 'portfolio-review',
        context: { track_title: track?.title || track?.name || trackSlug },
      });
      setPortfolioAiResult(result);
      setPortfolioAiState('done');
    } catch (error) {
      setPortfolioAiError(error.message || 'Portfolio review failed.');
      setPortfolioAiState('error');
    }
  };

  const runInterviewPrep = async () => {
    setInterviewPrepState('loading');
    setInterviewPrepError('');
    try {
      const markdown = buildLessonsMarkdown(track, progressCache, flatLessons);
      const result = await analyzeEvidence({
        content: markdown,
        analysisType: 'interview-prep',
        context: { track_title: track?.title || track?.name || trackSlug },
      });
      setInterviewPrepResult(result);
      setInterviewPrepState('done');
    } catch (error) {
      setInterviewPrepError(error.message || 'Interview prep failed.');
      setInterviewPrepState('error');
    }
  };

  const runSecondary = async (analysisType, title) => {
    setSecondaryOpen(true);
    setSecondaryTitle(title);
    setSecondaryAnalysisType(analysisType);
    setSecondaryState('loading');
    setSecondaryError('');
    try {
      const markdown = buildLessonsMarkdown(track, progressCache, flatLessons);
      const result = await analyzeEvidence({
        content: markdown,
        analysisType,
        context: { track_title: track?.title || track?.name || trackSlug },
      });
      setSecondaryResult(result);
      setSecondaryState('done');
    } catch (error) {
      setSecondaryError(error.message || 'Generation failed.');
      setSecondaryState('error');
    }
  };

  const practiceItem = useMemo(() => {
    if (!lessonDetail) return null;
    // Keep SOLVE/5-HOW as a rehearsal surface, not a repeated block on every lesson.
    const practiceCadencePattern = /-(practice|review|weekly-review|exam-recall)$/;
    const isPracticeDay = practiceCadencePattern.test(selectedSlug || '');
    const hasFrameworks =
      Array.isArray(lessonDetail.practice_frameworks) &&
      lessonDetail.practice_frameworks.length > 0;
    const hasModelAnswer = Boolean(lessonDetail.solve_model_answer?.trim());
    if (!isPracticeDay && !hasModelAnswer) return null;
    const taskType = resolveLessonTaskType(lessonDetail);
    // SOLVE/5-HOW was built for Python coding problems (breaking down logic, tracing
    // code, debugging) — it doesn't fit SQL/Excel/data-analysis lessons, where it just
    // re-explains the same scenario in a second, disconnected format. Restrict it to
    // actual coding lessons only.
    if (taskType !== 'python') return null;
    const conceptTools = extractLessonTools(lessonDetail, taskType);
    const fiveHowContent = lessonDetail.concept?.trim()
      ? buildWorkedExamples(lessonDetail, taskType)
      : undefined;
    const setupSql = taskType === 'sql' ? SQL_SAMPLE_SETUP : '';
    // Extract the opening business scenario from the concept (before the first --- rule)
    const conceptRaw = lessonDetail.concept?.trim() || '';
    const concept_scenario = conceptRaw
      ? (conceptRaw.split(/\n\n?---/)[0] || '').trim()
      : '';
    return {
      sourceType: 'lesson',
      sourceKey: lessonDetail.slug,
      title: lessonDetail.title,
      prompt: concept_scenario || lessonDetail.lesson_prompt,
      availableFrameworks:
        hasFrameworks &&
          lessonDetail.practice_frameworks.length > 0
          ? lessonDetail.practice_frameworks
          : ['solve', 'five-how'],
      defaultFramework: lessonDetail.recommended_framework || 'solve',
      context: {
        task_type: taskType,
        task_description: lessonDetail.deliverable || lessonDetail.lesson_prompt,
        target_question: lessonDetail.lesson_prompt,
        concept_scenario,
        ...(setupSql ? { setup_sql: setupSql } : {}),
      },
      stepOverrides: {
        solve: buildLessonSolveOverrides(lessonDetail, taskType, conceptTools),
      },
      guidance: lessonDetail.solve_model_answer
        ? { solve: { evolve: lessonDetail.solve_model_answer } }
        : undefined,
      fiveHowContent,
      similarProblems: buildSimilarProblems(lessonDetail, taskType),
    };
  }, [lessonDetail, selectedSlug]);

  const handlePracticeComplete = async (responses, framework) => {
    if (!practiceItem || !trackSlug || !selectedSlug) return;
    try {
      const entry = formatPracticeJournalEntry(practiceItem, responses, framework);
      const curProg = progressCache[selectedSlug] || {};
      // Keep SOLVE and 5-HOW entries side-by-side — only replace the matching framework block
      const updatedMarkdown = upsertPracticeEntry(curProg.practice_markdown || '', entry, framework);
      await saveLessonProgress(selectedSlug, {
        completed_task_indices: curProg.completed_task_indices || [],
        notes: curProg.notes || '',
        follow_up_topics: curProg.follow_up_topics || '',
        is_completed: curProg.is_completed || false,
        confidence: curProg.confidence || 0,
        practice_markdown: updatedMarkdown,
      });
      const newProg = {
        ...curProg,
        practice_markdown: updatedMarkdown,
        updated_at: new Date().toISOString(),
      };
      const newCache = { ...progressCache, [selectedSlug]: newProg };
      setProgressCache(newCache);
      const markdown = buildLessonsMarkdown(track, newCache, flatLessons);
      await saveLessonsJournal(trackSlug, markdown);
    } catch { /* journal is best-effort */ }
  };

  // ── Loading / error states ─────────────────────────────────────────────────

  if (trackLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (trackError || !track) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, px: 2 }}>
        <Alert severity="error">{trackError || 'Track not found.'}</Alert>
      </Box>
    );
  }

  const completedLessons = flatLessons.filter((l) => l.is_completed).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>

      {/* ── Hero band ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: track.color || '#0f1c14',
          color: '#fff',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            justifyContent={{ xs: 'center', md: 'space-between' }}
            spacing={{ xs: 2, md: 2 }}
            sx={{ py: { xs: 2.5, md: 3.5 }, textAlign: { xs: 'center', md: 'left' } }}
          >
            {/* Left: identity */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <ProgressRing value={overallProgress} size={52} stroke={3} />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    display: 'block',
                    mb: 0.25,
                  }}
                >
                  {track.target_audience || 'Learning path'}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, lineHeight: 1.15, color: '#fff', wordBreak: 'break-word' }}
                >
                  {track.name}
                </Typography>
              </Box>
            </Stack>

            {/* Right: stats + CTA — stacks vertically on mobile, row on desktop */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems="center"
              spacing={{ xs: 1.5, md: 2 }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {/* Stats */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#fff', lineHeight: 1 }}>
                    {completedLessons}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                    done
                  </Typography>
                </Box>
                <Box sx={{ width: '1px', height: 32, bgcolor: 'rgba(255,255,255,0.12)' }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#fff', lineHeight: 1 }}>
                    {flatLessons.length}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                    lessons
                  </Typography>
                </Box>
                <Box sx={{ width: '1px', height: 32, bgcolor: 'rgba(255,255,255,0.12)' }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#fff', lineHeight: 1 }}>
                    {overallCompletedTasks}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                    tasks
                  </Typography>
                </Box>
              </Stack>

              {/* CTAs */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => nextSlug && goToSlug(nextSlug)}
                  sx={{
                    bgcolor: '#fff',
                    color: track.color || '#0f1c14',
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  Continue →
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPortfolioReviewOpen(true)}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.72rem',
                    textTransform: 'none',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  Review portfolio
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={runCoach}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.72rem',
                    textTransform: 'none',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  Journal coach
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportLessons}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '0.72rem',
                    textTransform: 'none',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  Download journal
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── Body: navigator + lesson ───────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 1.5, md: 3 }, flex: 1, minWidth: 0 }}>

        {/* ── Horizontal filter bar ─────────────────────────────────────────── */}
        <Box sx={{ mb: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>

          {/* Phase tabs */}
          <Tabs
            value={phaseTabIndex}
            onChange={(_, v) => jumpToPhase(v)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { height: 2, backgroundColor: theme.palette.primary.main } }}
            sx={{
              minHeight: 42,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: alpha(theme.palette.text.primary, 0.02),
              '& .MuiTab-root': {
                minHeight: 42, py: 1, px: 2,
                textTransform: 'none', fontSize: '0.85rem', fontWeight: 500,
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
              },
            }}
          >
            {phaseChunks.map((chunk) => (
              <Tab key={chunk.courseSlug} label={chunk.phase} />
            ))}
          </Tabs>

          {/* Week/module stepper — prev/next + a dropdown to jump directly, replacing a
              full row of chips (some tracks have 40 weeks — that many chips at once is
              more clutter than navigation). */}
          {currentChunk && activeModuleIndex !== -1 && currentChunk.modules[activeModuleIndex] && (() => {
            const activeModule = currentChunk.modules[activeModuleIndex];
            const completedCount = activeModule.lessons.filter((l) => l.is_completed).length;
            const totalModules = currentChunk.modules.length;
            return (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 1,
                borderBottom: '1px solid', borderColor: 'divider',
              }}>
                <IconButton
                  size="small"
                  disabled={activeModuleIndex <= 0}
                  onClick={() => goToModule(currentChunk.modules[activeModuleIndex - 1])}
                  aria-label="Previous week"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Box
                  onClick={(e) => setWeekMenuAnchor(e.currentTarget)}
                  sx={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75,
                    cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 700 }}>
                    {activeModuleIndex + 1}/{totalModules}
                  </Typography>
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={700}
                    title={activeModule.name}
                    sx={{
                      flex: '1 1 0%',
                      minWidth: 0,
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                  >
                    {activeModule.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${completedCount}/${activeModule.lessons.length}`}
                    sx={{ flexShrink: 0, height: 20, fontSize: '0.68rem' }}
                  />
                  <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                </Box>
                <IconButton
                  size="small"
                  disabled={activeModuleIndex >= totalModules - 1}
                  onClick={() => goToModule(currentChunk.modules[activeModuleIndex + 1])}
                  aria-label="Next week"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={weekMenuAnchor}
                  open={Boolean(weekMenuAnchor)}
                  onClose={() => setWeekMenuAnchor(null)}
                  PaperProps={{ sx: { maxHeight: 360, width: 320 } }}
                >
                  {currentChunk.modules.map((module, idx) => {
                    const moduleCompleted = module.lessons.filter((l) => l.is_completed).length;
                    return (
                      <MenuItem
                        key={module.slug}
                        selected={idx === activeModuleIndex}
                        onClick={() => {
                          goToModule(module);
                          setWeekMenuAnchor(null);
                        }}
                        sx={{ whiteSpace: 'normal' }}
                      >
                        <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                          {module.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {moduleCompleted}/{module.lessons.length}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Box>
            );
          })()}

          {/* Lesson selector — compact chips for lessons in the active module */}
          {currentChunk && activeModuleIndex !== -1 && currentChunk.modules[activeModuleIndex] && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              px: 1.5,
              py: 1,
              gap: 0.75,
            }}>
              {currentChunk.modules[activeModuleIndex].lessons.map((lesson) => {
                const isSelected = lesson.slug === selectedSlug;
                const isLocked = !unlockedSlugs.has(lesson.slug);
                const completedCount = lesson.completed_task_count || 0;
                const totalCount = lesson.total_task_count || 0;
                return (
                  <Chip
                    key={lesson.slug}
                    label={`${compactLessonLabel(displayLessonTitle(lesson, track, trackSlug, {
                      trimRepeatedTopic: true,
                      contextTopic: currentChunk.modules[activeModuleIndex].name,
                    }))} · ${completedCount}/${totalCount}`}
                    size="small"
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    onClick={() => !isLocked && goToSlug(lesson.slug)}
                    title={displayLessonTitle(lesson, track, trackSlug, {
                      trimRepeatedTopic: true,
                      contextTopic: currentChunk.modules[activeModuleIndex].name,
                    })}
                    sx={{
                      fontSize: '0.76rem',
                      maxWidth: { xs: '100%', sm: 260 },
                      cursor: isLocked ? 'default' : 'pointer',
                      opacity: isLocked ? 0.45 : 1,
                      '& .MuiChip-label': {
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      ...(lesson.is_completed && !isSelected && { textDecoration: 'line-through', color: 'text.disabled' }),
                    }}
                  />
                );
              })}
            </Box>
          )}

        </Box>

        {/* ── Full-width lesson content ──────────────────────────────────────── */}
        <Box sx={{ minWidth: 0 }}>
            {lessonLoading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 400 }}>
                <CircularProgress size={32} />
              </Box>
            ) : lessonDetail ? (
              <>
                {/* Lesson meta bar */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    {currentLesson?.phase && (
                      <Chip label={currentLesson.phase} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                    )}
                    {currentLesson?.slug === nextSlug && (
                      <Chip label="Up next" size="small" color="primary" sx={{ fontSize: '0.68rem' }} />
                    )}
                    {currentLesson?.is_completed && (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label="Complete"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.68rem' }}
                      />
                    )}
                  </Stack>

                  {/* Lesson progress inline */}
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 160 }}>
                    <LinearProgress
                      variant="determinate"
                      value={dayProgress}
                      sx={{ flex: 1, height: 4, borderRadius: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                      {completedIndices.length}/{lessonDetail.tasks.length} tasks
                    </Typography>
                  </Stack>
                </Stack>

                <LessonView
                  key={lessonDetail.slug}
                  lesson={lessonDetail}
                  chips={[]}
                  completedIndices={completedIndices}
                  onToggleTask={toggleTask}
                  taskProgress={dayProgress}
                  practiceItem={practiceItem}
                  onPracticeComplete={handlePracticeComplete}
                  onNextLesson={nextLesson ? () => setSelectedSlug(nextLesson.slug) : null}
                  displayTitle={currentDisplayTitle}
                  nextLessonTitle={nextDisplayTitle}
                  confidence={currentProgress.confidence || 0}
                  onConfidenceChange={setLessonConfidence}
                  notesContent={
                    <>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ mb: 1.5 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {lessonDetail.lesson_prompt}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<HelpOutlineIcon />}
                          onClick={() => setJournalHelpOpen(true)}
                          sx={{ flexShrink: 0, textTransform: 'none' }}
                        >
                          How to write this
                        </Button>
                      </Stack>

                      {/* Notes input */}
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1.5,
                          pt: 1,
                          pb: 0.5,
                          mb: 1.5,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 0.75 }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              textTransform: 'uppercase',
                            }}
                          >
                            Evidence notes
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<HelpOutlineIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setJournalHelpOpen(true)}
                            sx={{ minWidth: 0, py: 0, textTransform: 'none', fontSize: '0.72rem' }}
                          >
                            Examples
                          </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Keep it concise: built, decision, evidence path, review next. Use backticks for code.
                        </Typography>
                        {noteItems.map((item, index) => (
                          <Stack
                            key={index}
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{ mb: 0.25 }}
                          >
                            <Typography
                              sx={{
                                color: 'text.disabled',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                lineHeight: 1,
                                minWidth: 18,
                                userSelect: 'none',
                              }}
                            >
                              –
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              variant="standard"
                              value={item}
                              placeholder={
                                index === 0 ? 'Built: what changed, in which file or tool?' : 'Decision, evidence path, or review next...'
                              }
                              onChange={(e) => {
                                const next = [...noteItems];
                                next[index] = e.target.value;
                                setNoteItems(next);
                                setNoteSaveStatus('Unsaved changes');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const next = [...noteItems];
                                  next.splice(index + 1, 0, '');
                                  setNoteItems(next);
                                } else if (e.key === 'Backspace' && !item && noteItems.length > 1) {
                                  e.preventDefault();
                                  setNoteItems(noteItems.filter((_, i) => i !== index));
                                }
                              }}
                              InputProps={{
                                disableUnderline: true,
                                sx: { fontSize: '0.9rem' },
                              }}
                            />
                            {noteItems.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setNoteItems(noteItems.filter((_, i) => i !== index))
                                }
                                sx={{ p: 0.25, color: 'text.disabled' }}
                              >
                                <CloseIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            )}
                          </Stack>
                        ))}
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            setNoteItems([...noteItems, '']);
                            setNoteSaveStatus('Unsaved changes');
                          }}
                          sx={{
                            mt: 0.5,
                            mb: 0.25,
                            color: 'text.secondary',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 0,
                          }}
                        >
                          Add point
                        </Button>
                        {notePreviewMarkdown && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.25,
                              borderRadius: 1,
                              bgcolor: 'background.default',
                              border: '1px dashed',
                              borderColor: 'divider',
                              '& p:last-child, & ol:last-child, & ul:last-child': { mb: 0 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mb: 0.75,
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                textTransform: 'uppercase',
                              }}
                            >
                              Preview
                            </Typography>
                            <MarkdownRenderer content={notePreviewMarkdown} />
                          </Box>
                        )}
                      </Box>

                      <FollowUpTopicsChecklist
                        value={followUpDraft}
                        onChange={setFollowUpDraft}
                        onDirty={() => setNoteSaveStatus('Unsaved changes')}
                      />

                      {/* Save + sync actions */}
                      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          flexWrap="wrap"
                          rowGap={1}
                        >
                          <Button
                            variant="contained"
                            onClick={saveLessonNotes}
                            fullWidth={false}
                            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                          >
                            Save journal evidence
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={runNoteCoach}
                            disabled={noteCoachState === 'loading'}
                            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                          >
                            Coach this note
                          </Button>

                          {learnerRepo.repo_url && learnerRepo.has_github_pat && (
                            <Button
                              variant="outlined"
                              startIcon={
                                githubPushState === 'pushing' ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <GitHubIcon />
                                )
                              }
                              onClick={pushToGitHub}
                              disabled={githubPushState === 'pushing'}
                              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
                            >
                              Push proof to GitHub
                            </Button>
                          )}
                        </Stack>

                        {noteSaveStatus && (
                          <Alert
                            severity={
                              noteSaveStatus.startsWith('Saved')
                                ? 'success'
                                : noteSaveStatus.includes('failed')
                                  ? 'error'
                                  : 'info'
                            }
                            sx={{ py: 0.5 }}
                          >
                            {noteSaveStatus}
                          </Alert>
                        )}

                        {githubPushResult && githubPushState === 'done' && (
                          <Alert
                            severity="success"
                            sx={{ py: 0.5 }}
                            action={
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                                href={githubPushResult.commit_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="inherit"
                              >
                                View commit
                              </Button>
                            }
                          >
                            Pushed to GitHub ({githubPushResult.commit_sha}).
                            {githubPushResult.pat_source && githubPushResult.pat_source !== track.learning_path_key
                              ? ` Used PAT from ${githubPushResult.pat_source}.`
                              : ''}
                          </Alert>
                        )}

                        {githubPushResult?.error && githubPushState === 'error' && (
                          <Alert
                            severity="error"
                            sx={{ py: 0.5 }}
                            action={
                              githubPushResult.error.toLowerCase().includes('pat') ||
                                githubPushResult.error.toLowerCase().includes('invalid') ||
                                githubPushResult.error.toLowerCase().includes('expired') ? (
                                <Button
                                  size="small"
                                  color="inherit"
                                  href="/account"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Fix in Settings
                                </Button>
                              ) : null
                            }
                          >
                            {githubPushResult.error}
                          </Alert>
                        )}

                        <Typography variant="caption" color="text.disabled">
                          GitHub push updates `journal.md`, the static `README.md` summary, and proof folders.
                          Full project files built in VS Code should still be committed from your repo workspace.
                        </Typography>

                        {(!learnerRepo.repo_url || !learnerRepo.has_github_pat) && (
                          <Alert severity="info" sx={{ py: 0.5 }}>
                            {!learnerRepo.repo_url
                              ? 'Add your GitHub repo URL and PAT in account settings to enable GitHub push.'
                              : 'GitHub repo saved. Add a PAT in account settings to enable push.'}
                          </Alert>
                        )}
                      </Stack>
                    </>
                  }
                />
              </>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  minHeight: 300,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Typography color="text.disabled">Select a lesson to get started.</Typography>
              </Box>
            )}

            <Dialog
              open={journalHelpOpen}
              onClose={() => setJournalHelpOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 800 }}>
                How to write useful journal notes
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2.5}>
                  <Alert severity="info">
                    Write notes like short evidence for your future interview. One point should usually
                    capture one idea: what changed, how you did it, or why it matters.
                  </Alert>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      First person vs recruiter-facing language
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Use first person in the journal when you are taking ownership or reflecting.
                      Use neutral outcome language when summarising proof for recruiters.
                    </Typography>
                    <Stack spacing={1}>
                      {JOURNAL_VOICE_EXAMPLES.map((example) => (
                        <Box
                          key={example.label}
                          sx={{
                            p: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                            {example.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Weak: {example.weak}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            Better: {example.strong}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Strong formats to use
                    </Typography>
                    <Stack spacing={0.75}>
                      {[
                        'Built: [specific output] using [tool/function/query], saved in [file path].',
                        'Decision: [choice made] because [business or production reason].',
                        'Evidence: [screenshot, command output, query result, dashboard, or file path].',
                        'Review next: [one concept, bug, tool, or interview question to revisit].',
                        'Keep long explanations as separate points instead of one paragraph.',
                      ].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.default',
                          }}
                        >
                          <Typography variant="body2">{item}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Example lesson notes
                    </Typography>
                    <Stack spacing={1}>
                      {JOURNAL_NOTE_EXAMPLES.map((example) => (
                        <Box
                          key={example.label}
                          sx={{
                            p: 1.25,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                            {example.label}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {example.text}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      What to put in "Topics to read more about"
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Add topics that would make tomorrow's work easier: a confusing term, a tool you used
                      without fully understanding, an error message, a documentation link, or an interview topic.
                    </Typography>
                    <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
                      {FOLLOW_UP_TOPIC_EXAMPLES.map((topic) => (
                        <Typography key={topic} component="li" variant="body2">
                          {topic}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Avoid weak notes
                    </Typography>
                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        Weak: "I learned Python today. I fixed the issue. I understand logging."
                      </Typography>
                      <Typography variant="body2">
                        Better: "I wrote a Python script that loads three CSV files in deterministic order with
                        `sorted(glob.glob(...))`. The fix prevents inconsistent row counts between my laptop and CI,
                        and it proves the pipeline can be reproduced."
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setJournalHelpOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
        </Box>
      </Container>

      <Dialog open={journalCoachOpen} onClose={() => setJournalCoachOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
          Journal coach
          <IconButton
            onClick={() => setJournalCoachOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {journalCoachState === 'loading' ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Reviewing your journal evidence...
              </Typography>
            </Stack>
          ) : journalCoachResult?.error ? (
            <Alert severity="error">{journalCoachResult.error}</Alert>
          ) : journalCoachResult ? (
            <Stack spacing={2.5}>
              {process.env.NODE_ENV === 'development' && (
                <Alert severity={journalCoachResult.mode === 'llm' ? 'success' : 'info'}>
                  {journalCoachResult.mode === 'llm'
                    ? `AI-enhanced review generated with ${journalCoachResult.model || 'configured model'}.`
                    : 'Rules-based review shown. Configure a local LLM to unlock richer rewrites.'}
                  {journalCoachResult.llm_error ? ` LLM fallback reason: ${journalCoachResult.llm_error}` : ''}
                </Alert>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {Object.entries(journalCoachResult.scores || {}).map(([label, value]) => (
                  <Chip
                    key={label}
                    label={`${label.replaceAll('_', ' ')}: ${value}`}
                    color={label === 'recruiter_readiness' ? 'primary' : 'default'}
                    variant={label === 'recruiter_readiness' ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Evidence detected
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75}>
                  {(journalCoachResult.tools_detected || []).map((tool) => (
                    <Chip key={tool} size="small" label={tool} />
                  ))}
                  {!journalCoachResult.tools_detected?.length && (
                    <Typography variant="body2" color="text.secondary">No specific tools detected yet.</Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Refactor recommendations
                </Typography>
                <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
                  {(journalCoachResult.recommendations || []).map((item) => (
                    <Typography key={item} component="li" variant="body2">{item}</Typography>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Weekly summary
                </Typography>
                <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                  <Typography variant="body2">{journalCoachResult.weekly_summary}</Typography>
                </Box>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    LinkedIn draft
                  </Typography>
                  <Button size="small" onClick={() => runSecondary('linkedin-post', 'LinkedIn post')}>
                    Generate full LinkedIn post
                  </Button>
                </Stack>
                <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default', whiteSpace: 'pre-wrap' }}>
                  <Typography variant="body2">{journalCoachResult.linkedin_post}</Typography>
                </Box>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    README bullets
                  </Typography>
                  <Button size="small" onClick={() => runSecondary('readme-summary', 'README summary')}>
                    Generate longer README
                  </Button>
                </Stack>
                <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
                  {(journalCoachResult.readme_bullets || []).map((item) => (
                    <Typography key={item} component="li" variant="body2">{item}</Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">Run the coach to review your current journal.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={runCoach} disabled={journalCoachState === 'loading'}>
            Run again
          </Button>
          <Button onClick={() => setJournalCoachOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={portfolioReviewOpen}
        onClose={() => setPortfolioReviewOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionProps={{
          onEnter: () => {
            if (portfolioAiState === 'idle') runPortfolioAi();
          },
          onExited: () => {
            setPortfolioAiState('idle');
            setInterviewPrepState('idle');
            setInterviewPrepResult(null);
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
          Portfolio review mode
          <IconButton
            onClick={() => setPortfolioReviewOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" icon={false}>
              This is a review lens, not another task. Use it weekly to check whether your repo would make sense to a recruiter, mentor, or interviewer.
            </Alert>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                AI portfolio review
              </Typography>
              <EvidenceIntelligenceSections
                state={portfolioAiState}
                result={portfolioAiResult}
                error={portfolioAiError}
                sections={['recommendations', 'gaps', 'strongest_claims']}
                loadingLabel="Reviewing your portfolio..."
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Reviewer path
              </Typography>
              <Typography component="ol" variant="body2" color="text.secondary" sx={{ pl: 2.5, m: 0, lineHeight: 1.8 }}>
                <li>Open `README.md` and confirm the target role, study path, and latest proof are clear.</li>
                <li>Open `journal.md` and read the latest entry for what changed, what was verified, and what to review next.</li>
                <li>Open the referenced project file, screenshot, query, notebook, dashboard, or architecture note.</li>
                <li>Check whether the work states the problem, evidence, validation, limitation, and next improvement.</li>
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Interview check
                </Typography>
                <Button size="small" onClick={runInterviewPrep} disabled={interviewPrepState === 'loading'}>
                  Generate interview prep
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                If you cannot explain the latest proof in 90 seconds, improve the README or journal before adding more lessons.
              </Typography>
              <EvidenceIntelligenceSections
                state={interviewPrepState}
                result={interviewPrepResult}
                error={interviewPrepError}
                sections={['interview_prompts', 'next_actions', 'strongest_claims']}
                loadingLabel="Preparing interview prompts..."
                idleHint="Generate interview prep to see STAR-style prompts."
              />
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <EvidenceIntelligenceDialog
        open={noteCoachOpen}
        onClose={() => setNoteCoachOpen(false)}
        title="Coach this note"
        state={noteCoachState}
        result={noteCoachResult}
        error={noteCoachError}
        onRerun={runNoteCoach}
        sections={['scores', 'recommendations', 'gaps', 'weekly_summary']}
        loadingLabel="Reviewing your note..."
        idleHint="Run the coach to review your current note."
      />

      <EvidenceIntelligenceDialog
        open={secondaryOpen}
        onClose={() => setSecondaryOpen(false)}
        title={secondaryTitle}
        state={secondaryState}
        result={secondaryResult}
        error={secondaryError}
        onRerun={() => runSecondary(secondaryAnalysisType, secondaryTitle)}
        sections={secondaryAnalysisType === 'linkedin-post' ? ['linkedin_post'] : ['readme_bullets']}
        loadingLabel="Generating..."
      />
    </Box>
  );
};

export default TrackRoadmapPage;
